"use client";

import { cn } from "@scott/ui/client";
import { format, getMonth, getYear, setMonth, setYear } from "date-fns";
import * as React from "react";
import { Body } from "../body";
import { Button } from "../button/button";
import { Calendar } from "../calendar/calendar";
import { CalendarIcon } from "../icon";
import { CloseIcon } from "../icon/icons/close-icon";
import { TickIcon } from "../icon/icons/tick-icon";
import { Label } from "../label";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select";

// Helper function to avoid nested ternaries
function getStatusTextColor(status?: "error" | "success" | "none"): string {
  if (status === "error") {
    return "text-danger-medium";
  }
  if (status === "success") {
    return "text-success-medium";
  }
  return "text-neutral-dark";
}

interface DatePickerProps {
  label?: string;
  message?: string;
  selectedDate?: Date;
  onChange: (date: Date) => void;
  status?: "error" | "success" | "none";
  startYear?: number;
  disabledDates?: (date: Date) => boolean;
  fallbackDate?: Date;
}

export function DatePicker({
  label,
  message,
  selectedDate,
  onChange,
  status,
  startYear = 1980,
  disabledDates,
  fallbackDate = new Date(),
}: DatePickerProps) {
  const [date, setDate] = React.useState<Date | undefined>(
    selectedDate ? new Date(selectedDate) : undefined
  );
  const [open, setOpen] = React.useState(false);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const endYear = new Date().getFullYear();
  const years = Array.from(
    { length: endYear - startYear + 1 },
    (_, i) => endYear - i
  );

  const handleMonthChange = (month: string) => {
    const monthIndex = months.indexOf(month);
    const newDate = date
      ? setMonth(date, monthIndex)
      : setMonth(new Date(), monthIndex);
    const isDisabled = disabledDates ? disabledDates(newDate) : false;
    const validDate = isDisabled ? fallbackDate : newDate;
    setDate(validDate);
    onChange(validDate);
  };

  const handleMonthChangeWithArrow = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      // Check if the selected date is disabled
      const isDisabled = disabledDates ? disabledDates(selectedDate) : false;
      // Use fallback date if selected date is disabled
      const validDate = isDisabled ? fallbackDate : selectedDate;
      setDate(validDate);
      onChange(validDate);
    }
  };

  const handleYearChange = (year: string) => {
    const yearNumber = Number.parseInt(year, 10);
    const newDate = date
      ? setYear(date, yearNumber)
      : setYear(new Date(), yearNumber);
    const isDisabled = disabledDates ? disabledDates(newDate) : false;
    const validDate = isDisabled ? fallbackDate : newDate;
    setDate(validDate);
    onChange(validDate);
  };

  const handleSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      // Check if the selected date is disabled
      const isDisabled = disabledDates ? disabledDates(selectedDate) : false;
      // Use fallback date if selected date is disabled
      const validDate = isDisabled ? fallbackDate : selectedDate;
      setDate(validDate);
      onChange(validDate);
      setOpen(false);
    }
  };

  return (
    <div className="mt-4">
      {label && <Label className="font-bold">{label}</Label>}
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild>
          <Button
            className={cn(
              "!mt-1 mb-1 w-full justify-start border-color-neutral px-3 py-6 text-left font-normal normal-case tracking-normal",
              !date && "text-muted-foreground",
              status === "error" && "border-danger-medium",
              status === "success" && "border-success-medium"
            )}
            size="sm"
            variant={"outline"}
          >
            <CalendarIcon
              className={cn(getStatusTextColor(status))}
              size="1x"
              title="Calendar icon"
            />
            {date ? format(date, "dd/MM/yyyy") : <span>Pick a date</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto overflow-hidden rounded-lg border border-neutral-medium p-0">
          <div className="flex justify-between gap-2 p-2">
            <Select
              onValueChange={handleMonthChange}
              value={date ? months[getMonth(date)] : ""}
            >
              <SelectTrigger>
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                {months.map((month) => (
                  <SelectItem key={month} value={month}>
                    {month}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              onValueChange={handleYearChange}
              value={date ? getYear(date).toString() : ""}
            >
              <SelectTrigger>
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Calendar
            autoFocus
            disabled={disabledDates}
            mode="single"
            month={date}
            onMonthChange={handleMonthChangeWithArrow}
            onSelect={handleSelect}
            selected={date}
          />
        </PopoverContent>
      </Popover>

      {status && (
        <div>
          {status === "error" && (
            <span className="flex items-center gap-1">
              <CloseIcon
                className="h-3 w-3 rounded-full text-error"
                size="sm"
              />
              <Body className="text-error" size="xs">
                {message}
              </Body>
            </span>
          )}
          {status === "success" && (
            <span className="flex items-center gap-1">
              <TickIcon className="h-3 w-3 text-button-success" size="sm" />
              <Body className="text-button-success" size="xs">
                {message}
              </Body>
            </span>
          )}
          {!status && message && (
            <span className="flex gap-1">
              <Body size="xs">{message}</Body>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
